import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, functions as functionsClient } from "../../firebase";
import useGameCatalog from "../../hooks/useGameCatalog";
import OverviewPanel from "./components/OverviewPanel";
import TournamentSettingsPanel from "./components/TournamentSettingsPanel";
import RegistrationsPanel from "./components/RegistrationsPanel";
import OperationsPanel from "./components/OperationsPanel";
import LobbyPanel from "./components/LobbyPanel";
import ScheduleModal from "./components/ScheduleModal";

const FORMAT_LABELS = {
  battle_royale: "Battle Royale",
  team_bracket: "Team Bracket",
  solo_bracket: "Solo Bracket",
};

const QUALIFICATION_LABELS = {
  placement: "Placement",
  totalPoints: "Total points",
  winner: "Match winner",
};

const BEST_OF_OPTIONS = [1, 3, 5];
const BRACKET_BASE_GAP = 16;
const DEFAULT_BRACKET_CARD_HEIGHT = 160;

const shuffle = (entries) => {
  const list = [...entries];
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
};

const chunk = (items, size) => {
  const result = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
};

const getNumber = (value) => {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const getNextPowerOfTwo = (value) => {
  let size = 1;
  while (size < value) {
    size *= 2;
  }
  return size;
};

const getBestOfWins = (bestOf) => Math.floor(getNumber(bestOf) / 2) + 1;
const formatBestOfLabel = (bestOf) => `Bo${getNumber(bestOf) || 1}`;

const parseDateValue = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") {
    return value.toDate();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const toLocalInputValue = (date) => {
  if (!date) return null;
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getFormat = (tournament, gameConfig) => {
  if (!tournament) return null;
  return (
    gameConfig?.format ||
    (tournament.type === "Solo" ? "solo_bracket" : "team_bracket")
  );
};

const getEntryType = (tournament, gameConfig) => {
  if (!tournament) return "team";
  if (gameConfig?.entryType) return gameConfig.entryType;
  return tournament.type === "Solo" ? "player" : "team";
};

const serializeEntry = (entry) => {
  if (entry.type === "player") {
    return {
      id: entry.id,
      type: "player",
      name: entry.name,
      userId: entry.userId,
      inGameName: entry.inGameName || "",
      inGameId: entry.inGameId || entry.inGameID || "",
      slot: entry.slot,
    };
  }
  const memberSource =
    entry.membersDetails?.length || entry.members?.length
      ? entry.membersDetails || entry.members
      : (entry.memberIds || []).map((id, index) => ({
          id,
          name: `Player ${index + 1}`,
        }));
  const members = (memberSource || []).map((member) => {
    if (typeof member === "string") {
      return {
        id: member,
        name: "Player",
        inGameName: "",
        inGameId: "",
      };
    }
    return {
      id: member.id || member.uid || member.userId || "",
      name: member.inGameName || member.username || member.name || "Player",
      inGameName: member.inGameName || "",
      inGameId: member.inGameID || member.inGameId || "",
      role: member.role || "",
    };
  });
  return {
    id: entry.id,
    type: "team",
    name: entry.name,
    memberIds: entry.memberIds || [],
    members,
    slot: entry.slot,
  };
};

const collectUserIds = (entries) => {
  const ids = new Set();
  entries.forEach((entry) => {
    if (entry.type === "player" && entry.userId) {
      ids.add(entry.userId);
      return;
    }
    if (entry.memberIds && entry.memberIds.length) {
      entry.memberIds.forEach((memberId) => ids.add(memberId));
    }
  });
  return Array.from(ids);
};

const ManageTournament = () => {
  const { id } = useParams();
  const { getGameConfig } = useGameCatalog();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registeredEntries, setRegisteredEntries] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [selectedRoundId, setSelectedRoundId] = useState("");
  const [lobbies, setLobbies] = useState([]);
  const [selectedLobbyId, setSelectedLobbyId] = useState("");
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState("");
  const [roundForm, setRoundForm] = useState({
    name: "",
    number: 1,
    teamsPerLobby: 16,
    autoCreate: true,
    scheduledStart: "",
    scheduledEnd: "",
    bestOf: 1,
    qualificationMethod: "totalPoints",
    qualifyCount: 0,
  });
  const [lobbyForm, setLobbyForm] = useState({
    name: "",
    number: 1,
  });
  const [matchForm, setMatchForm] = useState({
    name: "",
    number: 1,
  });
  const [selectedEntryId, setSelectedEntryId] = useState("");
  const [roomId, setRoomId] = useState("");
  const [roomPassword, setRoomPassword] = useState("");
  const [battleResults, setBattleResults] = useState([]);
  const [bracketResult, setBracketResult] = useState({
    scoreA: "",
    scoreB: "",
    winnerId: "",
  });
  const [bracketRounds, setBracketRounds] = useState([]);
  const [bracketLoading, setBracketLoading] = useState(false);
  const [bracketConnectors, setBracketConnectors] = useState([]);
  const [bracketTreeSize, setBracketTreeSize] = useState({
    width: 0,
    height: 0,
  });
  const [bracketCardHeight, setBracketCardHeight] = useState(0);
  const bracketTreeRef = useRef(null);
  const bracketCardRefs = useRef(new Map());
  const bracketConnectorFrame = useRef(null);
  const [activePanel, setActivePanel] = useState("operations");
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [nextRoundSchedule, setNextRoundSchedule] = useState({
    start: "",
    end: "",
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [backfillingRegisteredCount, setBackfillingRegisteredCount] =
    useState(false);
  const [backfillRegisteredCountResult, setBackfillRegisteredCountResult] =
    useState(null);
  const [error, setError] = useState("");

  const gameConfig = useMemo(
    () => getGameConfig(tournament?.gameId),
    [getGameConfig, tournament?.gameId]
  );
  const format = useMemo(
    () => getFormat(tournament, gameConfig),
    [gameConfig, tournament]
  );
  const isBracketFormat =
    format === "solo_bracket" || format === "team_bracket";
  const lobbyLabel = isBracketFormat ? "Matchup" : "Lobby";
  const lobbyLabelPlural = isBracketFormat ? "Matchups" : "Lobbies";
  const gameName = tournament ? gameConfig?.name || tournament.gameId : "";
  const storedRegisteredCount = useMemo(() => {
    if (typeof tournament?.registeredCount === "number") {
      return Math.max(0, tournament.registeredCount);
    }
    const parsed = Number(tournament?.registeredCount);
    return Number.isNaN(parsed) ? null : Math.max(0, parsed);
  }, [tournament?.registeredCount]);
  const bestOfValue = getNumber(roundForm.bestOf) || 1;
  const placementPointsMap = useMemo(() => {
    const points = Array.isArray(tournament?.placementPoints)
      ? tournament.placementPoints
      : [];
    return points.reduce((acc, item) => {
      const position = getNumber(item.position);
      const score = getNumber(item.points);
      if (position > 0 && score >= 0) {
        acc[position] = score;
      }
      return acc;
    }, {});
  }, [tournament]);

  const getPlacementPoints = useCallback(
    (placement) => {
      const position = getNumber(placement);
      return placementPointsMap[position] ?? 0;
    },
    [placementPointsMap]
  );

  const registeredEntryMap = useMemo(
    () =>
      registeredEntries.reduce((acc, entry) => {
        acc[entry.id] = entry;
        return acc;
      }, {}),
    [registeredEntries]
  );

  const normalizeMember = useCallback((member, index) => {
    if (typeof member === "string") {
      return {
        id: member,
        name: `Player ${index + 1}`,
        inGameName: "",
        inGameId: "",
      };
    }
    const id = member.id || member.uid || member.userId || `player-${index + 1}`;
    return {
      id,
      name: member.inGameName || member.username || member.name || `Player ${index + 1}`,
      inGameName: member.inGameName || "",
      inGameId: member.inGameID || member.inGameId || "",
      role: member.role || "",
    };
  }, []);

  const buildMemberList = useCallback(
    (entry, registered) => {
      const source =
        entry.members?.length
          ? entry.members
          : entry.membersDetails?.length
          ? entry.membersDetails
          : registered?.membersDetails?.length
          ? registered.membersDetails
          : null;
      if (source) {
        return source.map((member, index) => normalizeMember(member, index));
      }
      const ids =
        entry.memberIds ||
        registered?.memberIds ||
        registered?.members ||
        [];
      return ids.map((id, index) =>
        normalizeMember({ id, name: `Player ${index + 1}` }, index)
      );
    },
    [normalizeMember]
  );

  const hydrateLobbyEntries = useCallback(
    (entries = []) =>
      entries.filter(Boolean).map((entry, index) => {
        const slot = entry.slot ?? index + 1;
        const registered = registeredEntryMap[entry.id];
        if (entry.type === "player") {
          return {
            ...entry,
            name:
              entry.name ||
              registered?.inGameName ||
              registered?.name ||
              "Player",
            inGameName: entry.inGameName || registered?.inGameName || "",
            inGameId: entry.inGameId || registered?.inGameId || "",
            slot,
          };
        }
        const members = buildMemberList(entry, registered);
        return {
          ...entry,
          members,
          memberIds:
            entry.memberIds ||
            registered?.memberIds ||
            members.map((member) => member.id).filter(Boolean),
          slot,
        };
      }),
    [buildMemberList, registeredEntryMap]
  );

  const loadBracketTree = useCallback(async () => {
    if (!id || !isBracketFormat) {
      setBracketRounds([]);
      setBracketLoading(false);
      return;
    }
    if (!rounds.length) {
      setBracketRounds([]);
      setBracketLoading(false);
      return;
    }
    setBracketLoading(true);
    try {
      const orderedRounds = [...rounds].sort(
        (a, b) => (a.roundNumber || 0) - (b.roundNumber || 0)
      );
      const nextRounds = await Promise.all(
        orderedRounds.map(async (round) => {
          const roundFormat = round.format || format;
          if (roundFormat === "battle_royale") {
            return { ...round, lobbies: [] };
          }
          const lobbiesSnap = await getDocs(
            query(
              collection(db, "tournaments", id, "rounds", round.id, "lobbies"),
              orderBy("lobbyNumber", "asc")
            )
          );
          const fetched = lobbiesSnap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));
          const hydrated = fetched.map((lobby) => ({
            ...lobby,
            entries: hydrateLobbyEntries(lobby.entries || []),
          }));
          const lobbiesWithMatches = await Promise.all(
            hydrated.map(async (lobby) => {
              const matchSnap = await getDocs(
                query(
                  collection(
                    db,
                    "tournaments",
                    id,
                    "rounds",
                    round.id,
                    "lobbies",
                    lobby.id,
                    "matches"
                  ),
                  orderBy("matchNumber", "asc")
                )
              );
              const matchDocs = matchSnap.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
              }));
              return { ...lobby, matches: matchDocs };
            })
          );
          return { ...round, lobbies: lobbiesWithMatches };
        })
      );
      setBracketRounds(nextRounds);
    } catch (err) {
      console.error("Failed to load bracket tree", err);
    } finally {
      setBracketLoading(false);
    }
  }, [id, isBracketFormat, rounds, format, hydrateLobbyEntries]);

  const getSuggestedNextRoundSchedule = (round) => {
    const roundStart = parseDateValue(round?.scheduledStart);
    const roundEnd = parseDateValue(round?.scheduledEnd);
    if (!roundStart || !roundEnd) {
      return { start: "", end: "" };
    }
    const duration = roundEnd.getTime() - roundStart.getTime();
    const nextStartDate = new Date(roundEnd.getTime());
    const nextEndDate =
      duration > 0 ? new Date(roundEnd.getTime() + duration) : null;
    return {
      start: toLocalInputValue(nextStartDate) || "",
      end: nextEndDate ? toLocalInputValue(nextEndDate) || "" : "",
    };
  };

  const openScheduleModal = () => {
    if (!selectedRound) {
      setError("Select a round to qualify.");
      return;
    }
    setError("");
    setNextRoundSchedule(getSuggestedNextRoundSchedule(selectedRound));
    setIsScheduleModalOpen(true);
  };

  const handleConfirmSchedule = async () => {
    const success = await createNextRoundFromResults({
      start: nextRoundSchedule.start,
      end: nextRoundSchedule.end,
    });
    if (success) {
      setIsScheduleModalOpen(false);
    }
  };

  const selectedRound = useMemo(
    () => rounds.find((round) => round.id === selectedRoundId) || null,
    [rounds, selectedRoundId]
  );

  const selectedLobby = useMemo(
    () => lobbies.find((lobby) => lobby.id === selectedLobbyId) || null,
    [lobbies, selectedLobbyId]
  );

  const selectedMatch = useMemo(
    () => matches.find((match) => match.id === selectedMatchId) || null,
    [matches, selectedMatchId]
  );
  const activeBestOf = isBracketFormat
    ? getNumber(selectedRound?.bestOf) || 1
    : null;
  const activeBestOfWins = activeBestOf ? getBestOfWins(activeBestOf) : null;
  const bracketMatchupTotal = bracketRounds.reduce(
    (total, round) => total + (round.lobbies?.length || 0),
    0
  );
  const bracketRoundsForTree = useMemo(
    () =>
      bracketRounds.filter(
        (round) => (round.format || format) !== "battle_royale"
      ),
    [bracketRounds, format]
  );

  const getMatchupSlots = (entries = []) => {
    const slots = [null, null];
    entries.filter(Boolean).forEach((entry, index) => {
      const slotIndex =
        entry.slot !== undefined && entry.slot !== null
          ? entry.slot - 1
          : index;
      if (slotIndex >= 0 && slotIndex < 2 && !slots[slotIndex]) {
        slots[slotIndex] = entry;
        return;
      }
      const fallbackIndex = slots[0] ? 1 : 0;
      if (!slots[fallbackIndex]) {
        slots[fallbackIndex] = entry;
      }
    });
    return slots;
  };

  const upsertEntryInSlot = (entries = [], slot, entry) => {
    const sanitized = entries
      .filter(Boolean)
      .map((item, index) => ({ ...item, slot: item.slot ?? index + 1 }));
    const nextEntry = { ...serializeEntry(entry), slot };
    const filtered = sanitized.filter(
      (item) => item.id !== nextEntry.id && item.slot !== slot
    );
    return [...filtered, nextEntry].sort(
      (a, b) => (a.slot ?? 99) - (b.slot ?? 99)
    );
  };

  const getMatchupSummary = (lobby, roundBestOf) => {
    const entries = Array.isArray(lobby.entries) ? lobby.entries : [];
    const [entryA, entryB] = getMatchupSlots(entries);
    if (!entryA || !entryB) {
      return { scoreA: null, scoreB: null, winnerId: "" };
    }
    const matches = Array.isArray(lobby.matches) ? lobby.matches : [];
    const resultMatches = matches.filter(
      (match) =>
        match.result &&
        (match.result.winnerId ||
          match.result.scoreA !== undefined ||
          match.result.scoreB !== undefined)
    );
    if (!resultMatches.length) {
      return { scoreA: null, scoreB: null, winnerId: "" };
    }
    const targetWins = getBestOfWins(roundBestOf || 1);
    if (resultMatches.length === 1) {
      const series = resultMatches[0].result || {};
      const rawScoreA = getNumber(series.scoreA);
      const rawScoreB = getNumber(series.scoreB);
      const hasExplicitScore = rawScoreA > 0 || rawScoreB > 0;
      if (series.winnerId) {
        return {
          scoreA: hasExplicitScore ? rawScoreA : null,
          scoreB: hasExplicitScore ? rawScoreB : null,
          winnerId: series.winnerId,
        };
      }
      if (hasExplicitScore) {
        const derivedWinner =
          rawScoreA === rawScoreB
            ? ""
            : rawScoreA >= targetWins
            ? entryA.id
            : rawScoreB >= targetWins
            ? entryB.id
            : "";
        return { scoreA: rawScoreA, scoreB: rawScoreB, winnerId: derivedWinner };
      }
    }
    let winsA = 0;
    let winsB = 0;
    resultMatches.forEach((match) => {
      const winner = match.result?.winnerId;
      if (!winner) return;
      if (winner === entryA.id) winsA += 1;
      if (winner === entryB.id) winsB += 1;
    });
    if (!winsA && !winsB) {
      return { scoreA: null, scoreB: null, winnerId: "" };
    }
    const computedWinner =
      winsA >= targetWins ? entryA.id : winsB >= targetWins ? entryB.id : "";
    return { scoreA: winsA, scoreB: winsB, winnerId: computedWinner };
  };

  const getRoundLabel = (round, fallbackIndex) => {
    if (!round) return `Round ${fallbackIndex + 1}`;
    if (round.roundName) return round.roundName;
    if (round.roundNumber) return `Round ${round.roundNumber}`;
    return `Round ${fallbackIndex + 1}`;
  };

  const getSeedPlaceholder = (
    roundsForTree,
    roundIndex,
    lobbyNumber,
    slotIndex
  ) => {
    if (roundIndex === 0) {
      return { name: "Awaiting seed", meta: "" };
    }
    const prevRound = roundsForTree[roundIndex - 1];
    const prevRoundLabel = getRoundLabel(prevRound, roundIndex - 1);
    const prevMatchupNumber =
      slotIndex === 0 ? lobbyNumber * 2 - 1 : lobbyNumber * 2;
    return {
      name: `Winner of Matchup ${prevMatchupNumber}`,
      meta: prevRoundLabel ? `From ${prevRoundLabel}` : "",
    };
  };

  const getRoundLayoutStyles = useCallback(
    (roundIndex) => {
      const cardHeight = bracketCardHeight || DEFAULT_BRACKET_CARD_HEIGHT;
      const baseSpacing = cardHeight + BRACKET_BASE_GAP;
      const scale = 2 ** roundIndex;
      const roundGap = Math.max(baseSpacing * scale - cardHeight, BRACKET_BASE_GAP);
      const roundOffset =
        roundIndex === 0 ? 0 : ((scale - 1) * baseSpacing) / 2;
      return {
        "--round-gap": `${roundGap}px`,
        "--round-offset": `${roundOffset}px`,
      };
    },
    [bracketCardHeight]
  );

  const availableEntries = useMemo(() => {
    const assigned = new Set();
    lobbies.forEach((lobby) => {
      (lobby.entries || []).forEach((entry) => assigned.add(entry.id));
    });
    return registeredEntries.filter((entry) => !assigned.has(entry.id));
  }, [lobbies, registeredEntries]);

  useEffect(() => {
    const loadTournament = async () => {
      setLoading(true);
      try {
        const tournamentRef = doc(db, "tournaments", id);
        const tournamentSnap = await getDoc(tournamentRef);
        if (!tournamentSnap.exists()) {
          setTournament(null);
          setLoading(false);
          return;
        }

        const data = { id: tournamentSnap.id, ...tournamentSnap.data() };
        setTournament(data);
        await loadRegistrations(data);
        await loadRounds();
      } catch (err) {
        console.error("Failed to load tournament", err);
        setError("Failed to load tournament.");
      } finally {
        setLoading(false);
      }
    };

    const loadRegistrations = async (tournamentData) => {
      const regSnap = await getDocs(
        collection(db, "tournaments", id, "registered")
      );
      const entries = regSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const config = getGameConfig(tournamentData.gameId);
      const entryType = getEntryType(tournamentData, config);
      if (entryType === "player") {
        const userIds = entries
          .map((entry) => entry.userId)
          .filter(Boolean);
        const uniqueIds = Array.from(new Set(userIds));
        const userDocs = await Promise.all(
          uniqueIds.map((uid) => getDoc(doc(db, "users", uid)))
        );
        const userMap = userDocs.reduce((acc, snap) => {
          if (snap.exists()) {
            acc[snap.id] = snap.data();
          }
          return acc;
        }, {});

        const players = entries.map((entry) => {
          const profile = userMap[entry.userId] || {};
          const gameProfile = Array.isArray(profile.selectedGames)
            ? profile.selectedGames.find(
                (game) => game.selectedGame === tournamentData.gameId
              )
            : null;
          const inGameName = gameProfile?.inGameName || "";
          const inGameId = gameProfile?.inGameID || gameProfile?.inGameId || "";
          return {
            id: entry.id,
            type: "player",
            userId: entry.userId,
            name: profile.username || inGameName || "Player",
            inGameName,
            inGameId,
            registeredAt: entry.registeredAt,
          };
        });
        setRegisteredEntries(players);
      } else {
        const teams = entries.map((entry) => ({
          id: entry.id,
          type: "team",
          teamId: entry.teamId || entry.id,
          name: entry.teamName || "Team",
          memberIds: entry.members || entry.membersDetails?.map((m) => m.id) || [],
          membersDetails: entry.membersDetails || [],
          registeredAt: entry.registeredAt,
        }));
        setRegisteredEntries(teams);
      }
    };

    const loadRounds = async () => {
      const roundsSnap = await getDocs(
        query(
          collection(db, "tournaments", id, "rounds"),
          orderBy("roundNumber", "asc")
        )
      );
      const fetched = roundsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setRounds(fetched);
      if (fetched.length) {
        setSelectedRoundId((prev) => prev || fetched[0].id);
      }
    };

    if (id) {
      loadTournament();
    }
  }, [getGameConfig, id]);

  useEffect(() => {
    const loadLobbies = async () => {
      if (!selectedRoundId) {
        setLobbies([]);
        setSelectedLobbyId("");
        setMatches([]);
        setSelectedMatchId("");
        return;
      }
      const lobbiesSnap = await getDocs(
        query(
          collection(db, "tournaments", id, "rounds", selectedRoundId, "lobbies"),
          orderBy("lobbyNumber", "asc")
        )
      );
      const fetched = lobbiesSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      const hydrated = fetched.map((lobby) => ({
        ...lobby,
        entries: hydrateLobbyEntries(lobby.entries || []),
      }));
      setLobbies(hydrated);
      if (!fetched.length) {
        setSelectedLobbyId("");
        setMatches([]);
        setSelectedMatchId("");
        return;
      }
      setSelectedLobbyId((prev) => {
        if (prev && fetched.some((lobby) => lobby.id === prev)) {
          return prev;
        }
        return fetched[0].id;
      });
    };

    loadLobbies();
  }, [id, selectedRoundId, hydrateLobbyEntries]);

  useEffect(() => {
    if (!Object.keys(registeredEntryMap).length) return;
    setLobbies((prev) =>
      prev.map((lobby) => ({
        ...lobby,
        entries: hydrateLobbyEntries(lobby.entries || []),
      }))
    );
  }, [hydrateLobbyEntries, registeredEntryMap]);

  useEffect(() => {
    const loadMatches = async () => {
      if (!selectedRoundId || !selectedLobbyId) {
        setMatches([]);
        setSelectedMatchId("");
        return;
      }
      const matchSnap = await getDocs(
        query(
          collection(
            db,
            "tournaments",
            id,
            "rounds",
            selectedRoundId,
            "lobbies",
            selectedLobbyId,
            "matches"
          ),
          orderBy("matchNumber", "asc")
        )
      );
      const fetched = matchSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setMatches(fetched);
      if (!fetched.length) {
        setSelectedMatchId("");
        return;
      }
      setSelectedMatchId((prev) => {
        if (prev && fetched.some((match) => match.id === prev)) {
          return prev;
        }
        return fetched[0].id;
      });
    };

    loadMatches();
  }, [id, selectedRoundId, selectedLobbyId]);

  useEffect(() => {
    if (!isBracketFormat) {
      setBracketRounds([]);
      return;
    }
    if (activePanel !== "operations") return;
    loadBracketTree();
  }, [activePanel, isBracketFormat, loadBracketTree, lobbies, matches]);

  useEffect(() => {
    if (!selectedLobby) {
      setRoomId("");
      setRoomPassword("");
      return;
    }

    setRoomId(selectedLobby.roomId || "");
    setRoomPassword(selectedLobby.roomPassword || "");

  }, [selectedLobby, format]);

  useEffect(() => {
    if (!selectedMatch || !selectedLobby) {
      setBattleResults([]);
      setBracketResult({ scoreA: "", scoreB: "", winnerId: "" });
      return;
    }

    if (format === "battle_royale") {
      const existing = selectedMatch.results || [];
      const entries = selectedLobby.entries || [];
      const nextResults = entries.map((entry) => {
        const current = existing.find((item) => item.id === entry.id) || {};
        const memberSource =
          entry.type === "team"
            ? (entry.members?.length
                ? entry.members
                : (entry.memberIds || []).map((id, index) => ({
                    id,
                    name: `Player ${index + 1}`,
                    inGameName: "",
                    inGameId: "",
                  })))
            : [];
        const basePlayers =
          entry.type === "team"
            ? memberSource.map((member, index) => ({
                id: member.id || member.name || `player-${index}`,
                name: member.inGameName || member.name || "Player",
                inGameName: member.inGameName || "",
                inGameId: member.inGameId || member.inGameID || "",
                kills: "",
              }))
            : [
                {
                  id: entry.userId || entry.id,
                  name: entry.inGameName || entry.name || "Player",
                  inGameName: entry.inGameName || "",
                  inGameId: entry.inGameId || entry.inGameID || "",
                  kills: "",
                },
              ];
        const storedPlayers = Array.isArray(current.playerKills)
          ? current.playerKills
          : [];
        const mergedPlayers = [
          ...basePlayers.map((player) => {
            const existingPlayer = storedPlayers.find(
              (stored) => stored.id === player.id
            );
            return {
              ...player,
              inGameId: player.inGameId || existingPlayer?.inGameId || "",
              inGameName: player.inGameName || existingPlayer?.inGameName || "",
              name: player.name || existingPlayer?.name || "Player",
              kills: existingPlayer?.kills ?? player.kills ?? "",
            };
          }),
          ...storedPlayers
            .filter(
              (stored) => !basePlayers.some((player) => player.id === stored.id)
            )
            .map((stored) => ({
              id: stored.id || stored.name,
              name: stored.inGameName || stored.name || "Player",
              inGameName: stored.inGameName || "",
              inGameId: stored.inGameId || stored.inGameID || "",
              kills: stored.kills ?? "",
            })),
        ];
        const teamKills = mergedPlayers.reduce(
          (sum, player) => sum + getNumber(player.kills),
          0
        );
        const placement = current.placement ?? "";
        const positionPoints =
          current.positionPoints ?? getPlacementPoints(placement);
        const totalPoints =
          current.totalPoints ?? getNumber(positionPoints) + teamKills;
        return {
          id: entry.id,
          name: entry.name,
          placement,
          wwcd: current.wwcd ?? false,
          kills: teamKills,
          positionPoints,
          totalPoints,
          playerKills: mergedPlayers,
        };
      });
      setBattleResults(nextResults);
    } else {
      const current = selectedMatch.result || {};
      setBracketResult({
        scoreA: current.scoreA ?? "",
        scoreB: current.scoreB ?? "",
        winnerId: current.winnerId ?? "",
      });
    }
  }, [selectedMatch, selectedLobby, format, getPlacementPoints]);

  useEffect(() => {
    if (!lobbies.length) {
      setLobbyForm((prev) => ({ ...prev, number: 1 }));
      return;
    }
    const nextNumber =
      Math.max(...lobbies.map((lobby) => lobby.lobbyNumber || 0)) + 1;
    setLobbyForm((prev) => ({
      ...prev,
      number: nextNumber,
      name:
        prev.name ||
        `${format === "battle_royale" ? "Lobby" : "Matchup"} ${nextNumber}`,
    }));
  }, [lobbies, format]);

  useEffect(() => {
    if (!matches.length) {
      setMatchForm((prev) => ({
        ...prev,
        number: 1,
        name: prev.name || "Match 1",
      }));
      return;
    }
    const nextNumber =
      Math.max(...matches.map((match) => match.matchNumber || 0)) + 1;
    setMatchForm((prev) => ({
      ...prev,
      number: nextNumber,
      name: prev.name || `Match ${nextNumber}`,
    }));
  }, [matches]);

  useEffect(() => {
    if (!rounds.length) return;
    const nextNumber =
      Math.max(...rounds.map((round) => round.roundNumber || 0)) + 1;
    setRoundForm((prev) => ({
      ...prev,
      number: nextNumber,
      name: prev.name || `Round ${nextNumber}`,
    }));
  }, [rounds]);

  useEffect(() => {
    if (!format) return;
    const allowed =
      format === "battle_royale"
        ? ["placement", "totalPoints"]
        : ["winner"];
    const nextMethod =
      format === "battle_royale" ? "totalPoints" : "winner";
    setRoundForm((prev) => {
      const next = { ...prev };
      if (!allowed.includes(prev.qualificationMethod)) {
        next.qualificationMethod = nextMethod;
        next.qualifyCount = format === "battle_royale" ? prev.qualifyCount : 0;
      }
      if (format !== "battle_royale") {
        next.bestOf = getNumber(prev.bestOf) || 1;
      }
      return next;
    });
  }, [format]);

  const buildBracketConnectors = useCallback(() => {
    const container = bracketTreeRef.current;
    if (!container) return;
    const roundsForTree = bracketRounds.filter(
      (round) => (round.format || format) !== "battle_royale"
    );
    if (!roundsForTree.length) {
      setBracketConnectors([]);
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const scrollTop = container.scrollTop;
    const width = container.scrollWidth;
    const height = container.scrollHeight;
    const firstCard = bracketCardRefs.current.values().next().value;
    if (firstCard) {
      const { height: cardHeight } = firstCard.getBoundingClientRect();
      if (cardHeight) {
        setBracketCardHeight((prev) =>
          Math.abs(prev - cardHeight) > 1 ? cardHeight : prev
        );
      }
    }
    const connectors = [];
    for (let i = 0; i < roundsForTree.length - 1; i += 1) {
      const round = roundsForTree[i];
      const nextRound = roundsForTree[i + 1];
      const groups = new Map();
      (round.lobbies || []).forEach((lobby) => {
        const lobbyNumber = lobby.lobbyNumber || 1;
        const nextLobbyNumber = Math.ceil(lobbyNumber / 2);
        const nextLobby = (nextRound.lobbies || []).find(
          (item) => item.lobbyNumber === nextLobbyNumber
        );
        if (!nextLobby) return;
        const groupKey = `${nextRound.id}:${nextLobby.id}`;
        if (!groups.has(groupKey)) {
          groups.set(groupKey, {
            nextLobby,
            sources: [],
          });
        }
        groups.get(groupKey).sources.push(lobby);
      });

      groups.forEach((group, groupKey) => {
        const toKey = `${nextRound.id}:${group.nextLobby.id}`;
        const toNode = bracketCardRefs.current.get(toKey);
        if (!toNode) return;
        const toRect = toNode.getBoundingClientRect();
        const x2 =
          toRect.left - containerRect.left + scrollLeft;
        const y2 =
          toRect.top -
          containerRect.top +
          scrollTop +
          toRect.height / 2;
        const sourcePoints = group.sources
          .map((lobby) => {
            const fromKey = `${round.id}:${lobby.id}`;
            const fromNode = bracketCardRefs.current.get(fromKey);
            if (!fromNode) return null;
            const fromRect = fromNode.getBoundingClientRect();
            return {
              key: fromKey,
              x: fromRect.right - containerRect.left + scrollLeft,
              y:
                fromRect.top -
                containerRect.top +
                scrollTop +
                fromRect.height / 2,
            };
          })
          .filter(Boolean);
        if (!sourcePoints.length) return;
        const maxStart = Math.max(...sourcePoints.map((point) => point.x));
        const span = Math.max(x2 - maxStart, 0);
        const elbow = Math.min(x2 - 12, maxStart + Math.max(18, span / 2));
        const yValues = sourcePoints.map((point) => point.y);
        const yMin = Math.min(...yValues);
        const yMax = Math.max(...yValues);
        const segments = sourcePoints.map(
          (point) => `M ${point.x} ${point.y} H ${elbow}`
        );
        if (sourcePoints.length > 1) {
          segments.push(`M ${elbow} ${yMin} V ${yMax}`);
        }
        segments.push(`M ${elbow} ${y2} H ${x2}`);
        connectors.push({
          id: `${groupKey}`,
          d: segments.join(" "),
        });
      });
    }
    setBracketTreeSize({ width, height });
    setBracketConnectors(connectors);
  }, [bracketRounds, format]);

  const scheduleBracketConnectors = useCallback(() => {
    if (bracketConnectorFrame.current) {
      cancelAnimationFrame(bracketConnectorFrame.current);
    }
    bracketConnectorFrame.current = requestAnimationFrame(() => {
      bracketConnectorFrame.current = null;
      buildBracketConnectors();
    });
  }, [buildBracketConnectors]);

  useEffect(() => {
    if (!isBracketFormat || activePanel !== "operations") return;
    scheduleBracketConnectors();
  }, [
    activePanel,
    isBracketFormat,
    scheduleBracketConnectors,
    bracketRounds,
    bracketCardHeight,
  ]);

  useEffect(() => {
    if (!isBracketFormat) return undefined;
    const handleResize = () => scheduleBracketConnectors();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isBracketFormat, scheduleBracketConnectors]);

  useEffect(() => {
    if (!isBracketFormat) return undefined;
    const container = bracketTreeRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      scheduleBracketConnectors();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [isBracketFormat, scheduleBracketConnectors]);

  useEffect(() => () => {
    if (bracketConnectorFrame.current) {
      cancelAnimationFrame(bracketConnectorFrame.current);
    }
  }, []);

  const buildSeededMatchups = (entries, matchupsCount) => {
    const seeds = [...entries];
    const byes = Math.max(matchupsCount * 2 - seeds.length, 0);
    const matchups = [];
    let index = 0;
    for (let i = 0; i < matchupsCount; i += 1) {
      if (i < byes) {
        if (seeds[index]) {
          matchups.push([seeds[index]]);
          index += 1;
        } else {
          matchups.push([]);
        }
        continue;
      }
      const first = seeds[index];
      const second = seeds[index + 1];
      index += 2;
      const pair = [];
      if (first) pair.push(first);
      if (second) pair.push(second);
      matchups.push(pair);
    }
    return matchups;
  };

  const createFullBracket = async () => {
    if (!registeredEntries.length) {
      setError("No registrations available to seed the bracket.");
      return;
    }
    if (rounds.length) {
      setError("Bracket rounds already exist. Delete them to rebuild.");
      return;
    }
    const baseRoundNumber = getNumber(roundForm.number) || 1;
    const bracketSize = Math.max(
      2,
      getNextPowerOfTwo(registeredEntries.length)
    );
    const totalRounds = Math.max(1, Math.log2(bracketSize));
    const roundOneMatchups = bracketSize / 2;
    const normalizedEntries = registeredEntries.map((entry) =>
      serializeEntry(entry)
    );
    const seededMatchups = buildSeededMatchups(
      normalizedEntries,
      roundOneMatchups
    );

    const baseStart = parseDateValue(roundForm.scheduledStart);
    const baseEnd = parseDateValue(roundForm.scheduledEnd);
    const durationMs =
      baseStart && baseEnd ? baseEnd.getTime() - baseStart.getTime() : 0;

    const getRoundSchedule = (index) => {
      if (!baseStart || !baseEnd || durationMs <= 0) {
        return {
          start: index === 0 ? roundForm.scheduledStart || null : null,
          end: index === 0 ? roundForm.scheduledEnd || null : null,
        };
      }
      const roundStart = new Date(baseStart.getTime() + durationMs * index);
      const roundEnd = new Date(baseEnd.getTime() + durationMs * index);
      return {
        start: toLocalInputValue(roundStart) || null,
        end: toLocalInputValue(roundEnd) || null,
      };
    };

    const nextRoundSeedMap = {};
    seededMatchups.forEach((pair, index) => {
      if (pair.length !== 1) return;
      const nextLobbyNumber = Math.ceil((index + 1) / 2);
      const slot = (index + 1) % 2 === 1 ? 1 : 2;
      const seedEntry = { ...pair[0], slot };
      nextRoundSeedMap[nextLobbyNumber] = upsertEntryInSlot(
        nextRoundSeedMap[nextLobbyNumber] || [],
        slot,
        seedEntry
      );
    });

    const operations = [];
    const nextRoundsState = [];
    let roundOneId = "";
    let roundOneLobbies = [];

    for (let i = 0; i < totalRounds; i += 1) {
      const roundNumber = baseRoundNumber + i;
      const roundName =
        i === 0 && roundForm.name
          ? roundForm.name
          : `Round ${roundNumber}`;
      const roundSchedule = getRoundSchedule(i);
      const roundRef = doc(collection(db, "tournaments", id, "rounds"));
      if (i === 0) {
        roundOneId = roundRef.id;
      }
      const roundPayload = {
        roundName,
        roundNumber,
        format,
        teamsPerLobby: null,
        scheduledStart: roundSchedule.start,
        scheduledEnd: roundSchedule.end,
        bestOf: getNumber(roundForm.bestOf) || 1,
        qualificationMethod: "winner",
        qualifyCount: null,
        createdAt: serverTimestamp(),
      };
      operations.push({ ref: roundRef, data: roundPayload });

      const matchupCount = roundOneMatchups / 2 ** i;
      const lobbies = [];

      for (let index = 0; index < matchupCount; index += 1) {
        let entries = [];
        if (i === 0) {
          const matchupEntries = seededMatchups[index] || [];
          entries = matchupEntries.map((entry, entryIndex) => ({
            ...entry,
            slot: entryIndex + 1,
          }));
        } else if (i === 1 && nextRoundSeedMap[index + 1]) {
          entries = nextRoundSeedMap[index + 1];
        }
        const lobbyPayload = {
          lobbyName: `Matchup ${index + 1}`,
          lobbyNumber: index + 1,
          format,
          status: "scheduled",
          entries,
          createdAt: serverTimestamp(),
        };
        const lobbyRef = doc(
          collection(db, "tournaments", id, "rounds", roundRef.id, "lobbies")
        );
        operations.push({ ref: lobbyRef, data: lobbyPayload });
        lobbies.push({ id: lobbyRef.id, ...lobbyPayload });
      }

      if (i === 0) {
        roundOneLobbies = lobbies;
      }

      nextRoundsState.push({ id: roundRef.id, ...roundPayload });
    }

    let batch = writeBatch(db);
    let batchCount = 0;
    for (const op of operations) {
      batch.set(op.ref, op.data);
      batchCount += 1;
      if (batchCount >= 450) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }
    }
    if (batchCount) {
      await batch.commit();
    }

    const hydratedRoundOneLobbies = roundOneLobbies.map((lobby) => ({
      ...lobby,
      entries: hydrateLobbyEntries(lobby.entries || []),
    }));
    setRounds(nextRoundsState);
    setSelectedRoundId(roundOneId);
    setLobbies(hydratedRoundOneLobbies);
    if (hydratedRoundOneLobbies.length) {
      setSelectedLobbyId(hydratedRoundOneLobbies[0].id || "");
    }
    setActivePanel("operations");
    loadBracketTree();
  };

  const createRound = async () => {
    if (!roundForm.name || !roundForm.number) {
      setError("Round name and number are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (isBracketFormat && roundForm.autoCreate) {
        await createFullBracket();
        return;
      }
      const roundPayload = {
        roundName: roundForm.name,
        roundNumber: Number(roundForm.number),
        format,
        teamsPerLobby:
          format === "battle_royale"
            ? Number(roundForm.teamsPerLobby) || null
            : null,
        scheduledStart: roundForm.scheduledStart || null,
        scheduledEnd: roundForm.scheduledEnd || null,
        bestOf:
          format === "battle_royale"
            ? null
            : getNumber(roundForm.bestOf) || 1,
        qualificationMethod:
          format === "battle_royale"
            ? roundForm.qualificationMethod || "totalPoints"
            : "winner",
        qualifyCount:
          format === "battle_royale"
            ? Number(roundForm.qualifyCount) || null
            : null,
        createdAt: serverTimestamp(),
      };
      const roundRef = await addDoc(
        collection(db, "tournaments", id, "rounds"),
        roundPayload
      );

      if (roundForm.autoCreate && registeredEntries.length) {
        await autoCreateLobbies(roundRef.id);
      }

      setRounds((prev) => {
        const next = [...prev, { id: roundRef.id, ...roundPayload }];
        return next.sort(
          (a, b) => (a.roundNumber || 0) - (b.roundNumber || 0)
        );
      });
      setRoundForm((prev) => ({
        ...prev,
        name: "",
        number: prev.number + 1,
        scheduledStart: "",
        scheduledEnd: "",
      }));
      setSelectedRoundId(roundRef.id);
    } catch (err) {
      console.error("Failed to create round", err);
      setError("Failed to create round.");
    } finally {
      setSaving(false);
    }
  };

  const autoCreateLobbies = async (roundId, seedEntries = registeredEntries, options = {}) => {
    const formatValue = options.format || format;
    if (!formatValue) return;
    const batch = writeBatch(db);
    const lobbyCollection = collection(
      db,
      "tournaments",
      id,
      "rounds",
      roundId,
      "lobbies"
    );
    const normalizedEntries = seedEntries.map((entry) =>
      entry.type ? entry : serializeEntry(entry)
    );

    if (formatValue === "battle_royale") {
      const teamsPerLobby =
        Number(options.teamsPerLobby ?? roundForm.teamsPerLobby) || 16;
      const grouped = chunk(shuffle(normalizedEntries), teamsPerLobby);
      grouped.forEach((group, index) => {
        const lobbyRef = doc(lobbyCollection);
        batch.set(lobbyRef, {
          lobbyName: `Lobby ${index + 1}`,
          lobbyNumber: index + 1,
          format: formatValue,
          status: "scheduled",
          entries: group,
          createdAt: serverTimestamp(),
        });
      });
    } else {
      const pairs = chunk([...normalizedEntries], 2);
      pairs.forEach((pair, index) => {
        const lobbyRef = doc(lobbyCollection);
        const slottedEntries = pair.map((entry, entryIndex) => ({
          ...entry,
          slot: entryIndex + 1,
        }));
        batch.set(lobbyRef, {
          lobbyName: `Matchup ${index + 1}`,
          lobbyNumber: index + 1,
          format: formatValue,
          status: "scheduled",
          entries: slottedEntries,
          createdAt: serverTimestamp(),
        });
      });
    }

    await batch.commit();
  };

  const createLobby = async () => {
    if (!selectedRoundId) return;
    if (!lobbyForm.name || !lobbyForm.number) {
      setError(`${lobbyLabel} name and number are required.`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const lobbyPayload = {
        lobbyName: lobbyForm.name,
        lobbyNumber: Number(lobbyForm.number),
        format,
        status: "scheduled",
        entries: [],
        createdAt: serverTimestamp(),
      };
      const lobbyRef = await addDoc(
        collection(db, "tournaments", id, "rounds", selectedRoundId, "lobbies"),
        lobbyPayload
      );
      setLobbies((prev) => {
        const next = [...prev, { id: lobbyRef.id, ...lobbyPayload }];
        return next.sort(
          (a, b) => (a.lobbyNumber || 0) - (b.lobbyNumber || 0)
        );
      });
      setSelectedLobbyId(lobbyRef.id);
      setLobbyForm((prev) => ({ ...prev, name: "" }));
    } catch (err) {
      console.error("Failed to create lobby", err);
      setError(`Failed to create ${lobbyLabel.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  const createMatch = async () => {
    if (!selectedRoundId || !selectedLobby) return;
    if (!matchForm.name || !matchForm.number) {
      setError("Match name and number are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const matchPayload = {
        matchName: matchForm.name,
        matchNumber: Number(matchForm.number),
        format,
        status: "scheduled",
        createdAt: serverTimestamp(),
      };
      const matchRef = await addDoc(
        collection(
          db,
          "tournaments",
          id,
          "rounds",
          selectedRoundId,
          "lobbies",
          selectedLobby.id,
          "matches"
        ),
        matchPayload
      );
      setMatches((prev) => {
        const next = [...prev, { id: matchRef.id, ...matchPayload }];
        return next.sort(
          (a, b) => (a.matchNumber || 0) - (b.matchNumber || 0)
        );
      });
      setSelectedMatchId(matchRef.id);
      setMatchForm((prev) => ({ ...prev, name: "" }));
    } catch (err) {
      console.error("Failed to create match", err);
      setError("Failed to create match.");
    } finally {
      setSaving(false);
    }
  };

  const deleteLobbyWithMatches = async (roundId, lobbyId) => {
    const matchSnap = await getDocs(
      collection(
        db,
        "tournaments",
        id,
        "rounds",
        roundId,
        "lobbies",
        lobbyId,
        "matches"
      )
    );
    const batch = writeBatch(db);
    matchSnap.docs.forEach((docSnap) => batch.delete(docSnap.ref));
    batch.delete(
      doc(db, "tournaments", id, "rounds", roundId, "lobbies", lobbyId)
    );
    await batch.commit();
  };

  const handleDeleteMatch = async (matchId) => {
    if (!selectedRoundId || !selectedLobby) return;
    const match = matches.find((item) => item.id === matchId);
    const label = match?.matchName || "this match";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await deleteDoc(
        doc(
          db,
          "tournaments",
          id,
          "rounds",
          selectedRoundId,
          "lobbies",
          selectedLobby.id,
          "matches",
          matchId
        )
      );
      const nextMatches = matches.filter((item) => item.id !== matchId);
      setMatches(nextMatches);
      if (selectedMatchId === matchId) {
        setSelectedMatchId(nextMatches[0]?.id || "");
      }
    } catch (err) {
      console.error("Failed to delete match", err);
      setError("Failed to delete match.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLobby = async (lobbyId, roundId = selectedRoundId) => {
    if (!roundId) return;
    const roundLobbies =
      roundId === selectedRoundId
        ? lobbies
        : bracketRounds.find((round) => round.id === roundId)?.lobbies || [];
    const lobby = roundLobbies.find((item) => item.id === lobbyId);
    const label = lobby?.lobbyName || `this ${lobbyLabel.toLowerCase()}`;
    if (!window.confirm(`Delete ${label}? This deletes all matches inside.`)) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await deleteLobbyWithMatches(roundId, lobbyId);
      if (roundId === selectedRoundId) {
        const nextLobbies = lobbies.filter((item) => item.id !== lobbyId);
        setLobbies(nextLobbies);
        if (selectedLobbyId === lobbyId) {
          setSelectedLobbyId(nextLobbies[0]?.id || "");
          setMatches([]);
          setSelectedMatchId("");
          if (!nextLobbies.length) {
            setActivePanel("operations");
          }
        }
      }
      setBracketRounds((prev) =>
        prev.map((round) =>
          round.id === roundId
            ? {
                ...round,
                lobbies: (round.lobbies || []).filter(
                  (item) => item.id !== lobbyId
                ),
              }
            : round
        )
      );
    } catch (err) {
      console.error("Failed to delete lobby", err);
      setError(`Failed to delete ${lobbyLabel.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRound = async (roundId) => {
    const round = rounds.find((item) => item.id === roundId);
    const label = round?.roundName || "this round";
    if (
      !window.confirm(
        `Delete ${label}? This removes all ${lobbyLabelPlural.toLowerCase()} and matches inside.`
      )
    ) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const lobbiesSnap = await getDocs(
        collection(db, "tournaments", id, "rounds", roundId, "lobbies")
      );
      for (const lobbyDoc of lobbiesSnap.docs) {
        const matchSnap = await getDocs(
          collection(
            db,
            "tournaments",
            id,
            "rounds",
            roundId,
            "lobbies",
            lobbyDoc.id,
            "matches"
          )
        );
        const batch = writeBatch(db);
        matchSnap.docs.forEach((docSnap) => batch.delete(docSnap.ref));
        batch.delete(lobbyDoc.ref);
        await batch.commit();
      }
      await deleteDoc(doc(db, "tournaments", id, "rounds", roundId));
      const nextRounds = rounds.filter((item) => item.id !== roundId);
      setRounds(nextRounds);
      if (selectedRoundId === roundId) {
        setSelectedRoundId(nextRounds[0]?.id || "");
        setSelectedLobbyId("");
        setMatches([]);
        setSelectedMatchId("");
        if (!nextRounds.length) {
          setActivePanel("operations");
          setLobbies([]);
        }
      }
    } catch (err) {
      console.error("Failed to delete round", err);
      setError("Failed to delete round.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddEntry = async () => {
    if (!selectedLobby || !selectedEntryId) return;
    const entry = registeredEntries.find((item) => item.id === selectedEntryId);
    if (!entry) return;
    const existingEntries = Array.isArray(selectedLobby.entries)
      ? selectedLobby.entries.filter(Boolean)
      : [];
    if (format !== "battle_royale") {
      const slots = getMatchupSlots(existingEntries);
      const emptySlotIndex = slots.findIndex((slotEntry) => !slotEntry);
      if (emptySlotIndex === -1) {
        setError("Bracket matches can only have two participants.");
        return;
      }
      if (existingEntries.some((item) => item.id === entry.id)) {
        setSelectedEntryId("");
        return;
      }
      const updatedEntries = upsertEntryInSlot(
        existingEntries,
        emptySlotIndex + 1,
        entry
      );
      setSaving(true);
      setError("");
      try {
        const lobbyRef = doc(
          db,
          "tournaments",
          id,
          "rounds",
          selectedRoundId,
          "lobbies",
          selectedLobby.id
        );
        await updateDoc(lobbyRef, {
          entries: updatedEntries,
          updatedAt: serverTimestamp(),
        });
        setLobbies((prev) =>
          prev.map((lobby) =>
            lobby.id === selectedLobby.id
              ? { ...lobby, entries: updatedEntries }
              : lobby
          )
        );
        setSelectedEntryId("");
      } catch (err) {
        console.error("Failed to add entry", err);
        setError("Failed to add entry.");
      } finally {
        setSaving(false);
      }
      return;
    }
    const updatedEntries = [...existingEntries, serializeEntry(entry)];
    setSaving(true);
    setError("");
    try {
      const lobbyRef = doc(
        db,
        "tournaments",
        id,
        "rounds",
        selectedRoundId,
        "lobbies",
        selectedLobby.id
      );
      await updateDoc(lobbyRef, {
        entries: updatedEntries,
        updatedAt: serverTimestamp(),
      });
      setLobbies((prev) =>
        prev.map((lobby) =>
          lobby.id === selectedLobby.id
            ? { ...lobby, entries: updatedEntries }
            : lobby
        )
      );
      setSelectedEntryId("");
    } catch (err) {
      console.error("Failed to add entry", err);
      setError("Failed to add entry.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveEntry = async (entryId) => {
    if (!selectedLobby) return;
    const updatedEntries = (selectedLobby.entries || []).filter(
      (entry) => entry.id !== entryId
    );
    setSaving(true);
    setError("");
    try {
      const lobbyRef = doc(
        db,
        "tournaments",
        id,
        "rounds",
        selectedRoundId,
        "lobbies",
        selectedLobby.id
      );
      await updateDoc(lobbyRef, {
        entries: updatedEntries,
        updatedAt: serverTimestamp(),
      });
      setLobbies((prev) =>
        prev.map((lobby) =>
          lobby.id === selectedLobby.id
            ? { ...lobby, entries: updatedEntries }
            : lobby
        )
      );
    } catch (err) {
      console.error("Failed to remove entry", err);
      setError("Failed to remove entry.");
    } finally {
      setSaving(false);
    }
  };

  const saveLobbyCredentials = async () => {
    if (!selectedLobby) return;
    setSaving(true);
    setError("");
    try {
      const lobbyRef = doc(
        db,
        "tournaments",
        id,
        "rounds",
        selectedRoundId,
        "lobbies",
        selectedLobby.id
      );
      await updateDoc(lobbyRef, {
        roomId: roomId.trim(),
        roomPassword: roomPassword.trim(),
        updatedAt: serverTimestamp(),
      });
      setLobbies((prev) =>
        prev.map((lobby) =>
          lobby.id === selectedLobby.id
            ? {
                ...lobby,
                roomId: roomId.trim(),
                roomPassword: roomPassword.trim(),
              }
            : lobby
        )
      );
    } catch (err) {
      console.error("Failed to save lobby credentials", err);
      setError(`Failed to save ${lobbyLabel.toLowerCase()} credentials.`);
    } finally {
      setSaving(false);
    }
  };

  const sendLobbyCredentials = async () => {
    if (!selectedLobby) return;
    if (!roomId.trim() || !roomPassword.trim()) {
      setError("Room ID and password are required before sending.");
      return;
    }
    const recipients = collectUserIds(selectedLobby.entries || []);
    if (!recipients.length) {
      setError(`No participants assigned to this ${lobbyLabel.toLowerCase()}.`);
      return;
    }
    setSending(true);
    setError("");
    try {
      const sendCredentials = httpsCallable(getFunctions(), "sendLobbyCredentials");
      await sendCredentials({
        tournamentId: id,
        roundId: selectedRoundId,
        lobbyId: selectedLobby.id,
        lobbyName: selectedLobby.lobbyName,
        roomId: roomId.trim(),
        roomPassword: roomPassword.trim(),
        userIds: recipients,
      });
      await updateDoc(
        doc(
          db,
          "tournaments",
          id,
          "rounds",
          selectedRoundId,
          "lobbies",
          selectedLobby.id
        ),
        {
          credentialsSentAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );
      setLobbies((prev) =>
        prev.map((lobby) =>
          lobby.id === selectedLobby.id
            ? { ...lobby, credentialsSentAt: new Date() }
            : lobby
        )
      );
    } catch (err) {
      console.error("Failed to send lobby credentials", err);
      setError(`Failed to send ${lobbyLabel.toLowerCase()} credentials.`);
    } finally {
      setSending(false);
    }
  };

  const backfillRegisteredCount = async () => {
    if (!id) return;
    setBackfillingRegisteredCount(true);
    setBackfillRegisteredCountResult(null);
    setError("");
    try {
      const backfillCount = httpsCallable(
        functionsClient,
        "backfillTournamentRegisteredCounts"
      );
      const response = await backfillCount({
        tournamentId: id,
      });
      const data = response?.data || {};
      const tournamentSnap = await getDoc(doc(db, "tournaments", id));
      const nextTournament = tournamentSnap.exists()
        ? { id: tournamentSnap.id, ...tournamentSnap.data() }
        : null;
      if (nextTournament) {
        setTournament(nextTournament);
      }
      const nextStoredRegisteredCount = nextTournament
        ? Number(nextTournament.registeredCount || 0)
        : null;
      setBackfillRegisteredCountResult({
        processedCount: Number(data.processedCount || 0),
        updatedCount: Number(data.updatedCount || 0),
        unchangedCount: Number(data.unchangedCount || 0),
        failedCount: Number(data.failedCount || 0),
        registeredCount:
          nextStoredRegisteredCount === null ||
          Number.isNaN(nextStoredRegisteredCount)
            ? null
            : nextStoredRegisteredCount,
        ranAt: new Date(),
      });
    } catch (err) {
      console.error("Failed to backfill tournament registered count", err);
      setError(err?.message || "Failed to backfill tournament registered count.");
    } finally {
      setBackfillingRegisteredCount(false);
    }
  };

  const advanceWinnerToNextRound = async (roundId, lobby, winnerId) => {
    if (!roundId || !lobby || !winnerId) return;
    if (!isBracketFormat) return;
    try {
      const orderedRounds = [...rounds].sort(
        (a, b) => (a.roundNumber || 0) - (b.roundNumber || 0)
      );
      const roundIndex = orderedRounds.findIndex(
        (round) => round.id === roundId
      );
      if (roundIndex === -1 || roundIndex >= orderedRounds.length - 1) {
        return;
      }
      const nextRound = orderedRounds[roundIndex + 1];
      const lobbyNumber = lobby.lobbyNumber || 1;
      const nextLobbyNumber = Math.ceil(lobbyNumber / 2);
      const slot = lobbyNumber % 2 === 1 ? 1 : 2;
      const winnerEntry = (lobby.entries || []).find(
        (entry) => entry?.id === winnerId
      );
      if (!winnerEntry) return;

      let nextRoundLobbies =
        bracketRounds.find((round) => round.id === nextRound.id)?.lobbies || [];
      if (!nextRoundLobbies.length) {
        const nextSnap = await getDocs(
          query(
            collection(db, "tournaments", id, "rounds", nextRound.id, "lobbies"),
            orderBy("lobbyNumber", "asc")
          )
        );
        nextRoundLobbies = nextSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
      }
      const targetLobby = nextRoundLobbies.find(
        (item) => item.lobbyNumber === nextLobbyNumber
      );
      if (!targetLobby) return;
      const existingSlots = getMatchupSlots(targetLobby.entries || []);
      if (existingSlots[slot - 1]?.id === winnerEntry.id) {
        return;
      }
      const updatedEntries = upsertEntryInSlot(
        targetLobby.entries || [],
        slot,
        winnerEntry
      );
      await updateDoc(
        doc(
          db,
          "tournaments",
          id,
          "rounds",
          nextRound.id,
          "lobbies",
          targetLobby.id
        ),
        {
          entries: updatedEntries,
          updatedAt: serverTimestamp(),
        }
      );
      setBracketRounds((prev) =>
        prev.map((round) =>
          round.id === nextRound.id
            ? {
                ...round,
                lobbies: (round.lobbies || []).map((item) =>
                  item.id === targetLobby.id
                    ? { ...item, entries: updatedEntries }
                    : item
                ),
              }
            : round
        )
      );
      if (nextRound.id === selectedRoundId) {
        setLobbies((prev) =>
          prev.map((item) =>
            item.id === targetLobby.id
              ? { ...item, entries: updatedEntries }
              : item
          )
        );
      }
    } catch (err) {
      console.error("Failed to auto-advance winner", err);
    }
  };

  const saveResults = async () => {
    if (!selectedLobby || !selectedMatch) return;
    setSaving(true);
    setError("");
    try {
      const matchRef = doc(
        db,
        "tournaments",
        id,
        "rounds",
        selectedRoundId,
        "lobbies",
        selectedLobby.id,
        "matches",
        selectedMatch.id
      );
      if (format === "battle_royale") {
        const payload = battleResults.map((result) => ({
          id: result.id,
          name: result.name,
          placement: result.placement,
          kills: getNumber(result.kills),
          wwcd: Boolean(result.wwcd),
          positionPoints: getNumber(result.positionPoints),
          totalPoints: getNumber(result.totalPoints),
          playerKills: Array.isArray(result.playerKills)
            ? result.playerKills.map((player) => ({
                id: player.id || player.name,
                name: player.name || player.id || "Player",
                inGameName: player.inGameName || "",
                inGameId: player.inGameId || player.inGameID || "",
                kills: getNumber(player.kills),
              }))
            : [],
        }));
        await updateDoc(matchRef, {
          results: payload,
          resultsUpdatedAt: serverTimestamp(),
        });
        setMatches((prev) =>
          prev.map((match) =>
            match.id === selectedMatch.id
              ? {
                  ...match,
                  results: payload,
                  resultsUpdatedAt: new Date(),
                }
              : match
          )
        );
      } else {
        await updateDoc(matchRef, {
          result: bracketResult,
          resultsUpdatedAt: serverTimestamp(),
        });
        const updatedMatches = matches.map((match) =>
          match.id === selectedMatch.id
            ? {
                ...match,
                result: bracketResult,
                resultsUpdatedAt: new Date(),
              }
            : match
        );
        setMatches(updatedMatches);
        if (selectedRound) {
          const summary = getMatchupSummary(
            { entries: selectedLobby.entries || [], matches: updatedMatches },
            selectedRound.bestOf
          );
          if (summary.winnerId) {
            await advanceWinnerToNextRound(
              selectedRound.id,
              selectedLobby,
              summary.winnerId
            );
          }
        }
      }
    } catch (err) {
      console.error("Failed to save results", err);
      setError("Failed to save results.");
    } finally {
      setSaving(false);
    }
  };

  const updateBattleResult = (entryId, field, value) => {
    setBattleResults((prev) =>
      prev.map((result) => {
        if (result.id !== entryId) return result;
        const next = { ...result, [field]: value };
        const teamKills = Array.isArray(next.playerKills)
          ? next.playerKills.reduce(
              (sum, player) => sum + getNumber(player.kills),
              0
            )
          : getNumber(next.kills);
        const positionPoints = getPlacementPoints(next.placement);
        const totalPoints = positionPoints + teamKills;
        return {
          ...next,
          kills: teamKills,
          positionPoints,
          totalPoints,
        };
      })
    );
  };

  const updatePlayerKills = (entryId, playerId, value) => {
    setBattleResults((prev) =>
      prev.map((result) => {
        if (result.id !== entryId) return result;
        const nextPlayers = Array.isArray(result.playerKills)
          ? result.playerKills.map((player) =>
              player.id === playerId
                ? { ...player, kills: value }
                : player
            )
          : [];
        const teamKills = nextPlayers.reduce(
          (sum, player) => sum + getNumber(player.kills),
          0
        );
        const positionPoints = getPlacementPoints(result.placement);
        const totalPoints = positionPoints + teamKills;
        return {
          ...result,
          playerKills: nextPlayers,
          kills: teamKills,
          positionPoints,
          totalPoints,
        };
      })
    );
  };

  const createNextRoundFromResults = async (scheduleOverride = {}) => {
    if (!selectedRound) {
      setError("Select a round to qualify.");
      return false;
    }

    const roundFormat = selectedRound.format || format;
    const qualificationMethod =
      selectedRound.qualificationMethod ||
      (roundFormat === "battle_royale" ? "totalPoints" : "winner");
    const qualifyCount = getNumber(selectedRound.qualifyCount);

    if (roundFormat === "battle_royale" && qualifyCount <= 0) {
      setError("Set qualification count before creating the next round.");
      return false;
    }

    setSaving(true);
    setError("");
    try {
      const lobbiesSnap = await getDocs(
        query(
          collection(db, "tournaments", id, "rounds", selectedRound.id, "lobbies"),
          orderBy("lobbyNumber", "asc")
        )
      );
      const lobbyDocs = lobbiesSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      const matchGroups = await Promise.all(
        lobbyDocs.map(async (lobby) => {
          const matchSnap = await getDocs(
            query(
              collection(
                db,
                "tournaments",
                id,
                "rounds",
                selectedRound.id,
                "lobbies",
                lobby.id,
                "matches"
              ),
              orderBy("matchNumber", "asc")
            )
          );
          return {
            lobby,
            matches: matchSnap.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            })),
          };
        })
      );

      let qualifiedEntries = [];

      if (roundFormat === "battle_royale") {
        const aggregated = {};
        matchGroups.forEach(({ lobby, matches }) => {
          matches.forEach((match) => {
            const matchResults = Array.isArray(match.results)
              ? match.results
              : [];
            matchResults.forEach((result) => {
              const entry =
                (lobby.entries || []).find((item) => item.id === result.id) || {
                  id: result.id,
                  name: result.name,
                  type: "team",
                };
              const placement = getNumber(result.placement);
              const positionPoints =
                result.positionPoints ?? getPlacementPoints(placement);
              const kills =
                result.kills ??
                (Array.isArray(result.playerKills)
                  ? result.playerKills.reduce(
                      (sum, player) => sum + getNumber(player.kills),
                      0
                    )
                  : 0);
              const totalPoints =
                result.totalPoints ?? getNumber(positionPoints) + getNumber(kills);
              if (!aggregated[entry.id]) {
                aggregated[entry.id] = {
                  entry,
                  placementTotal: 0,
                  matches: 0,
                  positionPoints: 0,
                  totalPoints: 0,
                  kills: 0,
                };
              }
              aggregated[entry.id].placementTotal += placement;
              aggregated[entry.id].matches += 1;
              aggregated[entry.id].positionPoints += getNumber(positionPoints);
              aggregated[entry.id].totalPoints += getNumber(totalPoints);
              aggregated[entry.id].kills += getNumber(kills);
            });
          });
        });

        const ranked = Object.values(aggregated)
          .map((item) => ({
            ...item,
            placementAvg: item.matches
              ? item.placementTotal / item.matches
              : 0,
          }))
          .sort((a, b) => {
            if (qualificationMethod === "placement") {
              if (a.placementAvg !== b.placementAvg) {
                return a.placementAvg - b.placementAvg;
              }
              if (b.totalPoints !== a.totalPoints) {
                return b.totalPoints - a.totalPoints;
              }
            } else {
              if (b.totalPoints !== a.totalPoints) {
                return b.totalPoints - a.totalPoints;
              }
              if (b.positionPoints !== a.positionPoints) {
                return b.positionPoints - a.positionPoints;
              }
            }
            if (b.kills !== a.kills) {
              return b.kills - a.kills;
            }
            return (a.entry.name || "").localeCompare(b.entry.name || "");
          });

        if (!ranked.length) {
          setError("No match results found for this round.");
          setSaving(false);
          return false;
        }

        qualifiedEntries = ranked
          .slice(0, Math.min(qualifyCount, ranked.length))
          .map((item) => item.entry);
      } else {
        const winners = [];
        matchGroups.forEach(({ lobby, matches }) => {
          matches.forEach((match) => {
            const winnerId = match.result?.winnerId;
            if (!winnerId) return;
            const entry = (lobby.entries || []).find(
              (item) => item.id === winnerId
            );
            if (entry) {
              winners.push(entry);
            }
          });
        });
        const uniqueWinners = Array.from(
          new Map(winners.map((entry) => [entry.id, entry])).values()
        );
        if (!uniqueWinners.length) {
          setError("No winners found for this round.");
          setSaving(false);
          return false;
        }
        qualifiedEntries = uniqueWinners;
      }

      const suggestedSchedule = getSuggestedNextRoundSchedule(selectedRound);
      const scheduledStart =
        scheduleOverride.start !== undefined
          ? scheduleOverride.start || null
          : suggestedSchedule.start || null;
      const scheduledEnd =
        scheduleOverride.end !== undefined
          ? scheduleOverride.end || null
          : suggestedSchedule.end || null;

      const nextNumber =
        Math.max(...rounds.map((round) => round.roundNumber || 0)) + 1;
      const roundPayload = {
        roundName: `Round ${nextNumber}`,
        roundNumber: nextNumber,
        format: roundFormat,
        teamsPerLobby:
          roundFormat === "battle_royale"
            ? Number(selectedRound.teamsPerLobby) || 16
            : null,
        scheduledStart,
        scheduledEnd,
        bestOf:
          roundFormat === "battle_royale"
            ? null
            : getNumber(selectedRound.bestOf) || 1,
        qualificationMethod:
          roundFormat === "battle_royale" ? qualificationMethod : "winner",
        qualifyCount:
          roundFormat === "battle_royale" ? qualifyCount : null,
        createdAt: serverTimestamp(),
      };

      const roundRef = await addDoc(
        collection(db, "tournaments", id, "rounds"),
        roundPayload
      );
      await autoCreateLobbies(roundRef.id, qualifiedEntries, {
        format: roundFormat,
        teamsPerLobby: roundPayload.teamsPerLobby,
      });

      setRounds((prev) => {
        const next = [...prev, { id: roundRef.id, ...roundPayload }];
        return next.sort(
          (a, b) => (a.roundNumber || 0) - (b.roundNumber || 0)
        );
      });
      setSelectedRoundId(roundRef.id);
      setActivePanel("operations");
      return true;
    } catch (err) {
      console.error("Failed to create next round", err);
      setError("Failed to create next round.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="card loading-card">
          <div className="loading-title">Loading tournament</div>
          <div className="loading-subtitle">Fetching rounds and lobbies.</div>
          <div className="loading-bar" />
        </div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="page">
        <div className="card empty-state">
          <div className="empty-title">Tournament not found</div>
          <div className="empty-subtitle">
            This tournament ID does not exist in Firestore.
          </div>
        </div>
      </div>
    );
  }

  const lobbyEntries = selectedLobby?.entries || [];
  const lobbySlotEntries = getMatchupSlots(lobbyEntries).filter(Boolean);
  const entryTypeLabel =
    format === "solo_bracket" ? "Players" : "Teams";
  const entryTypeLabelSingle = entryTypeLabel.endsWith("s")
    ? entryTypeLabel.slice(0, -1)
    : entryTypeLabel;
  const panelOptions = [
    { id: "overview", label: "Overview" },
    { id: "settings", label: "Settings" },
    { id: "registrations", label: "Registrations" },
    { id: "operations", label: `Rounds & ${lobbyLabelPlural}` },
    { id: "lobby", label: `${lobbyLabel} workspace` },
  ];

  const formatDateTime = (value) => {
    if (!value) return "TBD";
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("en-GB");
  };

  const bracketViewBoxWidth = bracketTreeSize.width || 1;
  const bracketViewBoxHeight = bracketTreeSize.height || 1;
  const formatLabel = FORMAT_LABELS[format] || "Format";
  const operationsState = {
    isBracketFormat,
    bracketMatchupTotal,
    bracketLoading,
    rounds,
    bracketRoundsForTree,
    selectedRoundId,
    selectedLobbyId,
    entryTypeLabel,
    entryTypeLabelSingle,
    lobbyLabel,
    lobbyLabelPlural,
    lobbies,
    selectedRound,
    selectedLobby,
    activeBestOf,
    activeBestOfWins,
    roundForm,
    bestOfValue,
    format,
    lobbyForm,
    saving,
  };
  const operationsActions = {
    setSelectedRoundId,
    setSelectedLobbyId,
    setActivePanel,
    handleDeleteLobby,
    createRound,
    setRoundForm,
    createLobby,
    setLobbyForm,
    handleDeleteRound,
    openScheduleModal,
  };
  const operationsHelpers = {
    getNumber,
    formatBestOfLabel,
    getBestOfWins,
    getMatchupSlots,
    getMatchupSummary,
    getRoundLabel,
    getRoundLayoutStyles,
    getSeedPlaceholder,
    formatDateTime,
  };
  const operationsConstants = {
    BEST_OF_OPTIONS,
    FORMAT_LABELS,
    QUALIFICATION_LABELS,
  };
  const operationsBracket = {
    bracketTreeRef,
    bracketCardRefs,
    bracketViewBoxWidth,
    bracketViewBoxHeight,
    bracketConnectors,
  };
  const lobbyState = {
    lobbyLabel,
    lobbyLabelPlural,
    selectedRound,
    selectedLobby,
    selectedMatch,
    roomId,
    roomPassword,
    sending,
    lobbyEntries,
    selectedEntryId,
    availableEntries,
    matches,
    matchForm,
    selectedMatchId,
    battleResults,
    format,
    isBracketFormat,
    activeBestOf,
    activeBestOfWins,
    lobbySlotEntries,
    bracketResult,
    entryTypeLabel,
    saving,
  };
  const lobbyActions = {
    setActivePanel,
    setRoomId,
    setRoomPassword,
    saveLobbyCredentials,
    sendLobbyCredentials,
    setSelectedEntryId,
    handleAddEntry,
    handleRemoveEntry,
    setMatchForm,
    createMatch,
    setSelectedMatchId,
    handleDeleteMatch,
    updateBattleResult,
    updatePlayerKills,
    setBracketResult,
    saveResults,
  };
  const lobbyHelpers = {
    formatBestOfLabel,
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Manage Tournament</h1>
          <p>Settings, rounds, lobbies, credentials, and results for {gameName}.</p>
        </div>
      </div>

      <div className="manage-tabs">
        {panelOptions.map((panel) => (
          <button
            key={panel.id}
            type="button"
            className={`manage-tab ${
              activePanel === panel.id ? "active" : ""
            }`}
            onClick={() => setActivePanel(panel.id)}
            disabled={panel.id === "lobby" && !selectedLobbyId}
          >
            {panel.label}
          </button>
        ))}
      </div>

      {error && <div className="alert error">{error}</div>}

      {activePanel === "overview" && (
        <OverviewPanel
          gameName={gameName}
          formatLabel={formatLabel}
          tournament={tournament}
          registeredCount={registeredEntries.length}
          storedRegisteredCount={storedRegisteredCount}
          entryTypeLabel={entryTypeLabel}
          roundsCount={rounds.length}
          selectedRoundName={selectedRound?.roundName}
          formatDateTime={formatDateTime}
          backfillingRegisteredCount={backfillingRegisteredCount}
          backfillRegisteredCountResult={backfillRegisteredCountResult}
          onBackfillRegisteredCount={backfillRegisteredCount}
        />
      )}

      {activePanel === "registrations" && (
        <RegistrationsPanel
          registeredEntries={registeredEntries}
          entryTypeLabel={entryTypeLabel}
        />
      )}

      {activePanel === "settings" && (
        <TournamentSettingsPanel
          tournament={tournament}
          registeredCount={registeredEntries.length}
          roundsCount={rounds.length}
          onTournamentUpdated={setTournament}
        />
      )}

      {activePanel === "operations" && (
        <OperationsPanel
          state={operationsState}
          actions={operationsActions}
          helpers={operationsHelpers}
          constants={operationsConstants}
          bracket={operationsBracket}
        />
      )}

      {activePanel === "lobby" && (
        <LobbyPanel
          state={lobbyState}
          actions={lobbyActions}
          helpers={lobbyHelpers}
        />
      )}

      {isScheduleModalOpen && (
        <ScheduleModal
          saving={saving}
          nextRoundSchedule={nextRoundSchedule}
          setNextRoundSchedule={setNextRoundSchedule}
          onClose={() => setIsScheduleModalOpen(false)}
          onConfirm={handleConfirmSchedule}
        />
      )}

    </div>
  );
};

export default ManageTournament;
